/**
 * Quiz Library Slice
 * Manages state for browsing and searching quizzes to add to learning paths
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchQuizzes } from "@/api/fetchQuizzes";
import type { Quiz } from "@/modules/learningCenter/types/quiz";

/**
 * Quiz library state
 */
export interface QuizLibraryState {
  quizzes: Quiz[];
  total: number;
  page: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: "active" | "inactive" | "all";
  loading: boolean;
  error: string | null;
}

/**
 * Initial state
 */
const initialState: QuizLibraryState = {
  quizzes: [],
  total: 0,
  page: 1,
  pageSize: 20,
  searchTerm: "",
  statusFilter: "active",
  loading: false,
  error: null,
};

/**
 * Fetch quizzes for the library
 */
export const fetchQuizzesForLibrary = createAsyncThunk(
  "quizLibrary/fetchQuizzes",
  async (
    params:
      | {
          page?: number;
          pageSize?: number;
          search?: string;
          isActive?: boolean;
        }
      | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchQuizzes({
        page: params?.page,
        pageSize: params?.pageSize,
      });
      return {
        data: response.data,
        total: response.totalCount,
        page: response.page,
        pageSize: response.pageSize,
        searchTerm: params?.search || "",
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch quiz library",
      );
    }
  },
);

/**
 * Quiz library slice
 */
const quizLibrarySlice = createSlice({
  name: "quizLibrary",
  initialState,
  reducers: {
    /**
     * Set search term (filtering handled client-side for now)
     */
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
      state.page = 1; // Reset to first page on search
    },

    /**
     * Set status filter
     */
    setStatusFilter(
      state,
      action: PayloadAction<"active" | "inactive" | "all">,
    ) {
      state.statusFilter = action.payload;
      state.page = 1;
    },

    /**
     * Set page number
     */
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },

    /**
     * Clear all filters
     */
    clearFilters(state) {
      state.searchTerm = "";
      state.statusFilter = "active";
      state.page = 1;
    },

    /**
     * Reset state
     */
    resetQuizLibrary() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch quizzes
      .addCase(fetchQuizzesForLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuizzesForLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.quizzes = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchQuizzesForLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchTerm,
  setStatusFilter,
  setPage,
  clearFilters,
  resetQuizLibrary,
} = quizLibrarySlice.actions;

export default quizLibrarySlice.reducer;
